import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Zap } from 'lucide-react'
import { emit } from '../../platform/analytics/analytics'
import { DisabledReason } from '../../platform/access/DisabledReason'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { AssistantPanel } from '../../platform/primitives/Panel'
import { MAX_ACTIONS, merchantsDraft, toWrite, type RuleDraft } from '../rules/draft'
import { useUnsavedPreview } from '../rules/RuleFlow'

/** The rules engine evaluates this many recent rows for an unsaved rule, so a count that reaches it is a floor. */
export const PREVIEW_WINDOW = 1000

/**
 * Merchants the user said "Not now" to, for this browser session only (Q3). A
 * module-level set, not storage: reloading the page brings the offer back.
 */
const dismissed = new Set<string>()
export const isDismissed = (merchant: string) => dismissed.has(merchant)
/** Test seam: the set outlives any one render by design. */
export const resetDismissed = () => dismissed.clear()

/**
 * `merchant_name equals <merchant>` → `set_category <category>`, plus
 * `add_tag` for each tag added in this session. No owner and no account (Q3):
 * the rule keys on the merchant alone. Applies to past transactions.
 */
export function ruleFromCorrection(merchant: string, categoryId: string, addedTags: readonly string[]): RuleDraft {
  const base = merchantsDraft([merchant], categoryId)
  const tags = addedTags.map((value) => ({ type: 'add_tag' as const, value }))
  return { ...base, actions: [...base.actions, ...tags].slice(0, MAX_ACTIONS) }
}

type Props = {
  merchant: string
  categoryId: string
  categoryName: string
  addedTags: readonly string[]
  /**
   * Save it. `after` runs once the rule is FULLY saved, any confirmed backfill
   * included. `onWrittenUncounted` runs when the rule exists but its backfill
   * count failed: the card then says "Rule created" and offers nothing more to create.
   * The returned promise settles when the save has: Create rule is held until then, so a
   * double click cannot start a second save.
   */
  onCreate: (
    draft: RuleDraft,
    after: () => void,
    onWrittenUncounted: () => void,
  ) => Promise<'done' | 'declined' | 'count_failed' | 'failed' | void> | void
  /** Hide the card: after a save, or on Not now. */
  onClose: () => void
  /** Why Create rule is off (the rule cap), if it is. */
  blocked?: string
}

/** A count that is not known is left out: sending 0 would record "unknown" as a genuine zero. */
const countProps = (n: number | undefined) => (n === undefined ? {} : { transaction_count: n })

const CAP_REASON_ID = 'txn-rule-cap'

const count = (n: number) => n.toLocaleString('en-CA')

export function MakeItARule({ merchant, categoryId, categoryName, addedTags, onCreate, onClose, blocked }: Props) {
  const draft = useMemo(() => ruleFromCorrection(merchant, categoryId, addedTags), [merchant, categoryId, addedTags])
  const body = useMemo(() => {
    const w = toWrite(draft)
    return { conditions: w.conditions, actions: w.actions, limit: 1 }
  }, [draft])
  const { data, isFetching, isError } = useUnsavedPreview(body)
  // `keepPreviousData` may answer an older draft: a count is shown only when nothing is in flight.
  const result = isFetching || isError ? undefined : data

  // Shown means the card appeared with an offer to act on, so it is counted once the preview has
  // settled, success OR failure; an unknown count is left out, never sent as 0. Accepting first
  // announces it too, so accepted can never exceed shown.
  const announced = useRef(false)
  const announceShown = useCallback((count: number | undefined) => {
    if (announced.current) return
    announced.current = true
    emit('rule_suggested_shown', countProps(count))
  }, [])
  const settled = !isFetching
  const known = result?.matchedCount
  useEffect(() => {
    if (settled) announceShown(known)
  }, [settled, known, announceShown])
  /** The rule is saved but its past-transactions count failed: it exists, so it must not be offered again. */
  const [created, setCreated] = useState<'declined' | 'count_failed' | null>(null)
  const [saving, setSaving] = useState(false)

  const tagText =
    addedTags.length === 0 ? '' : ` and the tag${addedTags.length === 1 ? '' : 's'} ${addedTags.map((t) => `#${t}`).join(', ')}`
  let countText: string | null = null
  if (isFetching) countText = 'Counting…'
  else if (!isError && data !== undefined) {
    const n = data.matchedCount
    const prefix = data.totalEvaluated >= PREVIEW_WINDOW ? 'at least ' : ''
    countText = `Would apply to ${prefix}${count(n)} past transaction${n === 1 ? '' : 's'}.`
  }

  if (created !== null) {
    return (
      <AssistantPanel className="txn-rule">
        <div className="txn-rule__head">
          <span className="txn-rule__icon" aria-hidden="true">
            <Icon glyph={Zap} size={16} />
          </span>
          <h3>Rule created</h3>
        </div>
        <p className="txn-rule__body">
          {'Everything matching '}
          <strong>{merchant}</strong>
          {` gets ${categoryName}${tagText}.`}
        </p>
        <p className="txn-rule__count" role="status">
          {created === 'declined'
            ? 'Applied to new transactions only.'
            : 'The number of past transactions it changed isn’t available.'}
        </p>
        <div className="txn-rule__actions">
          <Button type="button" size="panel" variant="outline" onClick={onClose}>
            Done
          </Button>
        </div>
      </AssistantPanel>
    )
  }

  return (
    <AssistantPanel className="txn-rule">
      <div className="txn-rule__head">
        <span className="txn-rule__icon" aria-hidden="true">
          <Icon glyph={Zap} size={16} />
        </span>
        <h3>Make it a rule</h3>
      </div>
      <p className="txn-rule__body">
        {'Everything matching '}
        <strong>{merchant}</strong>
        {` gets ${categoryName}${tagText}.`}
      </p>
      {countText !== null && (
        <p className="txn-rule__count" aria-live="polite">
          {countText}
        </p>
      )}
      <div className="txn-rule__actions">
        {blocked !== undefined ? (
          <DisabledReason reason={blocked} reasonId={CAP_REASON_ID}>
            <Button
              type="button"
              size="panel"
              variant="primary"
              aria-disabled="true"
              aria-describedby={CAP_REASON_ID}
            >
              Create rule
            </Button>
          </DisabledReason>
        ) : (
          <Button
            type="button"
            size="panel"
            variant="primary"
            disabled={saving}
            onClick={() => {
              if (saving) return
              setSaving(true)
              announceShown(result?.matchedCount)
              let accepted = false
              const markCreated = (why: 'declined' | 'count_failed') => {
                if (accepted) return
                accepted = true
                emit('rule_suggested_accepted', countProps(result?.matchedCount))
                setCreated(why)
              }
              const saved = onCreate(
                draft,
                () => {
                  accepted = true
                  emit('rule_suggested_accepted', countProps(result?.matchedCount))
                  onClose()
                },
                () => markCreated('count_failed'),
              )
              // The rule exists though its backfill was declined or its count failed,
              // so it must not be offered again.
              void Promise.resolve(saved)
                .then((outcome) => {
                  if (outcome === 'declined' || outcome === 'count_failed') markCreated(outcome)
                })
                .finally(() => setSaving(false))
            }}
          >
            Create rule
          </Button>
        )}
        <Button
          type="button"
          size="panel"
          variant="outline"
          onClick={() => {
            dismissed.add(merchant)
            onClose()
          }}
        >
          Not now
        </Button>
      </div>
    </AssistantPanel>
  )
}
